import type { FC, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import ChevronDownIcon from '@untitled-ui/icons-react/build/esm/ChevronDown';
import ChevronUpIcon from '@untitled-ui/icons-react/build/esm/ChevronUp';
import {
  Box,
  ButtonBase,
  Paper,
  Portal,
  SvgIcon,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { RouterLink } from 'src/components/router-link';

const TOP_NAV_HEIGHT = 64;
const TOP_NAV_SPACE = 16;
const OFFSET = 16;

interface TopNavItemProps {
  active?: boolean;
  external?: boolean;
  path?: string;
  popover?: ReactNode;
  title: string;
  onClick?: (e: React.MouseEvent) => void;
}

export const TopNavItem: FC<TopNavItemProps> = ({
  active,
  external,
  path,
  popover,
  title,
  onClick
}) => {
  const [open, setOpen] = useState(false);

  const handleMouseEnter = useCallback(() => setOpen(true), []);
  const handleMouseLeave = useCallback(() => setOpen(false), []);

  // --- External link (opens in new tab) ---
  if (external && path) {
    return (
      <Box component="li" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <ButtonBase
          component="a"
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          disableRipple
          sx={{
            alignItems: 'center',
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'flex-start',
            px: 2,
            py: 1,
            color: 'black', // Always black text
            textAlign: 'left',
            borderBottom: active ? '2px solid #1976d2' : '2px solid transparent',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderBottom: '2px solid #1976d2',
              color: 'black', // Always black on hover
            },
            ...(active && { 
              color: 'black', // Always black even if active
              borderBottom: '2px solid #1976d2'
            })
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'black' }}>{title}</Typography>
        </ButtonBase>
      </Box>
    );
  }

  // --- Popover (mega menu style) ---
  if (popover) {
    return (
      <>
        <Box
          component="li"
          sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <ButtonBase
            disableRipple
            onClick={onClick}
            sx={{
              alignItems: 'center',
              borderRadius: 1,
              display: 'flex',
              justifyContent: 'flex-start',
              px: 2,
              py: 1,
              color: 'black', // Always black text
              textAlign: 'left',
              borderBottom: active ? '2px solid #1976d2' : '2px solid transparent',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderBottom: '2px solid #1976d2',
                color: 'black', // Always black on hover
              },
              ...(active && { 
                color: 'black', // Always black even if active
                borderBottom: '2px solid #1976d2'
              })
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'black' }}>{title}</Typography>
            <SvgIcon sx={{ fontSize: 16, ml: 1 }}>
              {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </SvgIcon>
          </ButtonBase>
        </Box>

        {open && (
          <Portal>
            <Box
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              sx={{
                left: 0,
                position: 'fixed',
                pt: OFFSET,
                right: 0,
                top: TOP_NAV_HEIGHT + TOP_NAV_SPACE,
                zIndex: (theme) => theme.zIndex.appBar + 100
              }}
            >
              <Paper
                elevation={16}
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.9),
                  backdropFilter: 'blur(6px)',
                  mx: 'auto',
                  maxWidth: 300,
                  width: 'auto',
                  position: 'relative',
                  left: '50%',
                  transform: 'translateX(-50%)'
                }}
              >
                {popover}
              </Paper>
            </Box>
          </Portal>
        )}
      </>
    );
  }

  // --- Internal navigation (with scroll or page route) ---
  return (
    <Box component="li" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <ButtonBase
        component={RouterLink}
        href={path ?? ''}
        onClick={(e) => {
          if (onClick && path?.startsWith('#')) {
            e.preventDefault(); // Prevent default scroll
            onClick(e);
          }
        }}
        disableRipple
        sx={{
          alignItems: 'center',
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'flex-start',
          px: 2,
          py: 1,
          color: 'black', // Always black text
          textAlign: 'left',
          borderBottom: active ? '2px solid #1976d2' : '2px solid transparent',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderBottom: '2px solid #1976d2',
            color: 'black', // Always black on hover
          },
          ...(active && { 
            color: 'black', // Always black even if active
            borderBottom: '2px solid #1976d2'
          })
        }}
      >
        <Typography variant="subtitle2" sx={{ color: 'black' }}>{title}</Typography>
      </ButtonBase>
    </Box>
  );
};

TopNavItem.propTypes = {
  active: PropTypes.bool,
  external: PropTypes.bool,
  path: PropTypes.string,
  popover: PropTypes.any,
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func
};
